/**
 * Program templates: sensible starting terms for the kinds of deal brands
 * actually run.
 *
 * A blank program form asks a first-time brand to decide a rate, a cookie
 * window, a payout schedule and a conversion-event table before they have
 * seen a single example. A template answers all of that with the numbers
 * the seed archetypes use, and the brand then edits what differs. It is a
 * starting point, never a recommendation of what to pay: every value is still
 * published as the brand's own term.
 *
 * Pure data, shared by the editor (which pre-fills the form) and the server
 * tests (which prove every template really is a valid program).
 *
 * `payload` is shaped like the body of POST /api/programs MINUS the things only
 * the brand can supply — title, destinationUrl, description — so applying a
 * template can never overwrite, or invent, any of them.
 */

export const PROGRAM_TEMPLATES = [
  {
    key: 'saas_recurring',
    name: 'SaaS — recurring revenue share',
    blurb: 'For subscription software: creators earn on every renewal for a year, plus a small bounty per free signup.',
    recommendedFor: 'Subscription software and apps',
    payload: {
      // Software is a digital product, which sets Affily's fee.
      kind: 'digital',
      commissionType: 'percent_of_sale',
      rateBps: 2000,
      recurringMode: 'fixed_period',
      recurringMonths: 12,
      cookieWindowDays: 60,
      payoutSchedule: 'net30',
      primaryEventKey: 'purchase',
      events: [
        {
          key: 'purchase',
          name: 'Paid plan',
          category: 'purchase',
          isPayable: true,
          countingMode: 'every',
        },
        // Pays something for the visitors who sign up and convert later, which a
        // creator's audience often does. Once per visitor: the same person
        // making two accounts is not two acquisitions.
        {
          key: 'signup',
          name: 'Free account',
          category: 'signup',
          isPayable: true,
          commissionType: 'flat_per_conversion',
          flatAmountCents: 150,
          countingMode: 'once_per_visitor',
        },
      ],
    },
  },
  {
    key: 'ecommerce_percent',
    name: 'Online store — % of each order',
    blurb: 'For shops selling products: a simple percentage of every order a creator sends.',
    recommendedFor: 'Online stores and DTC brands',
    payload: {
      commissionType: 'percent_of_sale',
      rateBps: 1500,
      recurringMode: 'one_time',
      cookieWindowDays: 30,
      payoutSchedule: 'net30',
    },
  },
  {
    key: 'event_tickets',
    name: 'Events & tickets — % of each ticket',
    blurb: 'For ticketed events: a percentage of every ticket a creator sells.',
    recommendedFor: 'Concerts, festivals, conferences and other ticketed events',
    payload: {
      commissionType: 'percent_of_sale',
      rateBps: 2000,
      recurringMode: 'one_time',
      cookieWindowDays: 30,
      payoutSchedule: 'net30',
      primaryEventKey: 'purchase',
      events: [
        {
          key: 'purchase',
          name: 'Ticket sale',
          category: 'purchase',
          isPayable: true,
          countingMode: 'every',
        },
      ],
    },
  },
  {
    key: 'lead_gen_flat',
    name: 'Leads — flat fee per signup or lead',
    blurb: 'For businesses that sell later: a fixed fee for each new lead or signup, counted once per visitor.',
    recommendedFor: 'Services, B2B and lead-driven businesses',
    payload: {
      commissionType: 'flat_per_conversion',
      flatAmountCents: 2000,
      recurringMode: 'one_time',
      cookieWindowDays: 30,
      payoutSchedule: 'net30',
      primaryEventKey: 'lead',
      events: [
        {
          key: 'lead',
          name: 'Lead',
          category: 'lead',
          isPayable: true,
          countingMode: 'once_per_visitor',
        },
      ],
    },
  },
  // Local businesses that take bookings. The site reports the booking (free to
  // make, so it pays nothing by itself); the business records the visit from it,
  // with the bill, once the client has actually come in. That is what pays.
  {
    key: 'salon_bookings',
    name: 'Salon & barber — % of first visit',
    blurb:
      'Your booking page reports each booking a creator brings. When the client has come in, you record the visit and its bill, and the creator earns a share of it.',
    recommendedFor: 'Hair salons, barbers, nail and beauty studios',
    payload: {
      commissionType: 'percent_of_sale',
      rateBps: 2000,
      recurringMode: 'one_time',
      cookieWindowDays: 30,
      payoutSchedule: 'net30',
      primaryEventKey: 'visit',
      events: [
        {
          key: 'visit',
          name: 'Visit',
          category: 'purchase',
          isPayable: true,
          manualOnly: true,
          countingMode: 'every',
        },
        {
          key: 'booking',
          name: 'Booking',
          category: 'lead',
          isPayable: false,
          countingMode: 'every',
        },
      ],
    },
  },
  {
    key: 'clinic_treatments',
    name: 'Clinic & med spa — % of treatment',
    blurb:
      'A consultation booked through a creator is tracked; when it becomes a paid treatment, you record it and the creator earns a share. A longer window, because people think before a treatment.',
    recommendedFor: 'Med spas, aesthetic and dental clinics',
    payload: {
      commissionType: 'percent_of_sale',
      rateBps: 1000,
      recurringMode: 'one_time',
      cookieWindowDays: 60,
      payoutSchedule: 'net30',
      primaryEventKey: 'treatment',
      events: [
        {
          key: 'treatment',
          name: 'Treatment',
          category: 'purchase',
          isPayable: true,
          manualOnly: true,
          countingMode: 'every',
        },
        {
          key: 'consultation',
          name: 'Consultation booked',
          category: 'lead',
          isPayable: false,
          countingMode: 'every',
        },
      ],
    },
  },
  {
    key: 'fitness_membership',
    name: 'Gym & studio — fixed fee per new member',
    blurb:
      'A trial class or visit booked through a creator is tracked; when that person buys a membership, you record it and the creator earns a fixed fee.',
    recommendedFor: 'Gyms, fitness, yoga and dance studios',
    payload: {
      commissionType: 'flat_per_conversion',
      flatAmountCents: 3000,
      recurringMode: 'one_time',
      cookieWindowDays: 30,
      payoutSchedule: 'net30',
      primaryEventKey: 'membership',
      events: [
        {
          key: 'membership',
          name: 'New member',
          category: 'purchase',
          isPayable: true,
          manualOnly: true,
          countingMode: 'every',
        },
        {
          key: 'trial',
          name: 'Trial booked',
          category: 'trial',
          isPayable: false,
          countingMode: 'every',
        },
      ],
    },
  },
  {
    key: 'ugc_flat_post',
    name: 'UGC / sponsored posts — fixed fee',
    blurb: 'For paying creators a set fee per approved post or video, whatever it sells.',
    recommendedFor: 'Brands commissioning content',
    payload: {
      commissionType: 'flat_per_post',
      flatAmountCents: 35_000,
      recurringMode: 'one_time',
      // A deliverable is paid when it is approved, not when a click converts, so
      // the cookie window is the shortest the platform allows.
      cookieWindowDays: 1,
      payoutSchedule: 'net15',
    },
  },
  {
    key: 'gifting_seeding',
    name: 'Gifting / product seeding',
    blurb: 'For getting product into creators’ hands: no commission, just a free gift to try.',
    recommendedFor: 'Launches and product seeding',
    payload: {
      commissionType: 'gift_only',
      recurringMode: 'one_time',
      cookieWindowDays: 30,
      payoutSchedule: 'net30',
      gifts: [
        {
          name: 'Product sample',
          fulfilment: 'ship',
          quantityTotal: 50,
          // Left blank on purpose: an invented price tag is worse than none.
          retailValueCents: null,
          isActive: true,
        },
      ],
    },
  },
  {
    key: 'creator_competition',
    name: 'Creator video competition',
    blurb:
      'Prizes for the videos that travel furthest and the bookings they bring, plus a bonus you record yourself when a booking becomes a sale. Runs three months.',
    recommendedFor: 'Bookings, viewings and events — anything a video can sell',
    // Its dates are set from the day it is applied (applyTemplate's `now`).
    durationDays: 90,
    payload: {
      // The bonus: a flat amount, paid only when the brand records the sale
      // from a booking a creator brought. The figure is a placeholder.
      commissionType: 'flat_per_conversion',
      flatAmountCents: 100_000,
      recurringMode: 'one_time',
      cookieWindowDays: 90,
      payoutSchedule: 'net30',
      // A campaign, not an evergreen program: it stops when the competition does.
      // A sale recorded later from a booking made while it ran still pays.
      autoExtend: false,
      primaryEventKey: 'sale',
      events: [
        {
          key: 'sale',
          name: 'Sale',
          category: 'purchase',
          isPayable: true,
          // A site cannot report it: only the brand knows a sale happened.
          manualOnly: true,
          countingMode: 'every',
        },
        {
          key: 'booking',
          name: 'Booking',
          category: 'lead',
          // Counted for the prize and the funnel; pays nothing by itself.
          isPayable: false,
          countingMode: 'every',
        },
      ],
      competition: {
        prizes: [
          { name: 'Most viewed video', metric: 'video_views', kind: 'most', amountCents: 50_000 },
          {
            name: 'First to 100,000 views',
            metric: 'video_views',
            kind: 'first_to',
            threshold: 100_000,
            amountCents: 25_000,
          },
          { name: 'Most shared video', metric: 'video_shares', kind: 'most', amountCents: 25_000 },
          { name: 'Most bookings', metric: 'event_count', eventKey: 'booking', amountCents: 35_000 },
        ],
      },
    },
  },
];

/**
 * "What kind of business is it?" — asked before the templates, so a barber
 * sees the salon template first rather than hunting through SaaS and tickets.
 * Each type lists its templates best first; the first is chosen for them.
 */
export const BUSINESS_TYPES = [
  { key: 'online_store', name: 'Online store', templates: ['ecommerce_percent', 'gifting_seeding', 'ugc_flat_post'] },
  { key: 'software', name: 'Software or app', templates: ['saas_recurring', 'lead_gen_flat', 'ugc_flat_post'] },
  { key: 'salon', name: 'Salon or barber', templates: ['salon_bookings', 'creator_competition', 'gifting_seeding'] },
  { key: 'clinic', name: 'Clinic or med spa', templates: ['clinic_treatments', 'creator_competition', 'lead_gen_flat'] },
  { key: 'fitness', name: 'Gym or studio', templates: ['fitness_membership', 'creator_competition', 'lead_gen_flat'] },
  { key: 'events', name: 'Events and tickets', templates: ['event_tickets', 'creator_competition', 'ugc_flat_post'] },
  {
    key: 'services',
    name: 'Services, B2B, property',
    templates: ['lead_gen_flat', 'creator_competition', 'ugc_flat_post'],
  },
];

/** A template by key, or undefined. Safe for untrusted input such as a URL param. */
export function getTemplate(key) {
  return PROGRAM_TEMPLATES.find((t) => t.key === key);
}

export const PROGRAM_TEMPLATE_KEYS = PROGRAM_TEMPLATES.map((t) => t.key);

/** What the "What are you selling?" picker starts on. */
export const DEFAULT_TEMPLATE_KEY = 'saas_recurring';

/**
 * A template's terms as a create-payload-shaped object, with `overrides` laid
 * on top (typically `{ title, destinationUrl }`).
 *
 * Always a deep copy: the editor mutates what it is given, and a template that
 * changed under one caller would change for every later one.
 */
export function applyTemplate(key, overrides = {}, { now = new Date() } = {}) {
  const template = getTemplate(key);
  if (!template) throw new Error(`Unknown program template: ${key}`);
  const payload = structuredClone(template.payload);
  // A campaign's dates run from today: a literal date in a template would go stale.
  if (template.durationDays) {
    const startsAt = new Date(now);
    const endsAt = new Date(startsAt.getTime() + template.durationDays * 86_400_000);
    payload.startsAt = startsAt.toISOString();
    payload.endsAt = endsAt.toISOString();
    if (payload.competition) {
      payload.competition.startsAt = payload.startsAt;
      payload.competition.endsAt = payload.endsAt;
    }
  }
  return { ...payload, ...overrides };
}
